import React, { useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../../config";
import { AuthImage, Button, HoverReveal, Panel, StatusPill, pluralise } from "./ui";

// Every extracted visual, visible. The point of this panel is that nobody should
// have to open the repo to find out whether a crop came out right.

const cropTone = (asset) => {
  if (asset.extraction_status === "failed") return ["red", "Extraction failed"];
  if (asset.extraction_status === "unavailable") return ["amber", "Extraction unavailable"];
  if (asset.extraction_status === "manual") return ["blue", "Uploaded by hand"];
  if (asset.crop_verification === "passed") return ["green", "Crop verified"];
  if (asset.crop_verification === "failed") return ["red", "Crop failed"];
  if (asset.crop_verification === "needs_review") return ["amber", "Needs a look"];
  return ["slate", "Not checked"];
};

const AssetCard = ({ asset, paperId, onChanged, busy, setBusy }) => {
  const [showSource, setShowSource] = useState(false);
  const [error, setError] = useState("");
  const [tone, cropLabel] = cropTone(asset);
  const approved = asset.human_review_status === "approved";
  const working = busy === asset.asset_id;
  const usedBy = asset.used_by || [];

  const act = async (path, options = {}) => {
    setBusy(asset.asset_id);
    setError("");
    try {
      const url = `${API_BASE_URL}/api/admin/robo/papers/${paperId}/assets/${asset.asset_id}/${path}`;
      const { data } = await axios.post(url, options.body);
      if (!data.success) throw new Error(data.error || "That did not work.");
      onChanged(data.paper);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setBusy(null);
    }
  };

  const replace = async (file) => {
    const form = new FormData();
    form.append("image", file);
    await act("replace", { body: form });
  };

  return (
    <div className={`rounded-xl border p-3 transition-colors ${
      approved ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.03]"
    }`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-white">
            Q{asset.owner_number} · {asset.type}
          </p>
          {/* The description is how you tell whether a crop shows the right thing, but
              at full length it pushes the image itself off the card. One line here,
              the whole thing on hover — no control to click, so nothing competes with
              the actual actions below. */}
          <div className="mt-0.5">
            {asset.description ? (
              <HoverReveal label="View Description">{asset.description}</HoverReveal>
            ) : (
              <span className="text-[11px] text-white/35">No description</span>
            )}
          </div>
        </div>
        {approved ? <StatusPill color="green">Approved</StatusPill> : <StatusPill color={tone}>{cropLabel}</StatusPill>}
      </div>

      <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-black/30">
        {asset.preview_url ? (
          <AuthImage
            src={`${API_BASE_URL}${asset.preview_url}`}
            version={asset.box_refined_at || asset.extraction_status}
            alt={`Question ${asset.owner_number} ${asset.type}`}
            className="max-h-56 w-full object-contain"
            emptyLabel="Could not load this crop"
          />
        ) : (
          <div className="flex h-32 items-center justify-center px-3 text-center text-xs text-white/40">
            {asset.extraction_error || "No image yet."}
          </div>
        )}
      </div>

      {/* The source page is not listed here — the button at the bottom of the card
          names it, and repeating it costs a line on an already tall card. */}
      <dl className="mt-3 space-y-1 text-[11px] text-white/50">
        {/* Which questions depend on this image decides how much a bad crop costs, so
            the full list stays reachable. A few show inline; a long one becomes a
            count that reveals every part on hover, rather than an ellipsis hiding
            exactly the questions worth checking. */}
        <div className="flex items-baseline justify-between gap-3">
          <dt className="shrink-0">Used by</dt>
          <dd className="min-w-0 text-right">
            {usedBy.length === 0 ? (
              <span className="text-white/70">—</span>
            ) : usedBy.length <= 2 ? (
              // One or two fit without help; hiding them behind a hover would be
              // more work to read, not less.
              <span className="inline-flex flex-wrap justify-end gap-1">
                {usedBy.map((n) => (
                  <span key={n} className="rounded bg-white/10 px-1.5 py-0.5 font-medium text-white/75">Q{n}</span>
                ))}
              </span>
            ) : (
              <HoverReveal label={`${usedBy.length} Questions`} width="w-56" align="right">
                <span className="flex flex-wrap gap-1">
                  {usedBy.map((n) => (
                    <span key={n} className="rounded bg-white/10 px-1.5 py-0.5 font-medium text-white/80">Q{n}</span>
                  ))}
                </span>
              </HoverReveal>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="shrink-0">File</dt>
          <dd className="truncate text-white/70">{asset.filename || "—"}</dd>
        </div>
      </dl>

      {asset.crop_verification_reason && (
        <p className="mt-2 rounded-md bg-white/5 px-2 py-1.5 text-[11px] leading-relaxed text-white/60">
          {asset.crop_verification_reason}
        </p>
      )}
      {error && <p className="mt-2 text-[11px] text-red-300">{error}</p>}

      {/* One row, no wrapping. The card is a third of the grid wide, so the actions
          share the width equally and shrink together rather than spilling onto a
          second line — flex-1 with min-w-0 is what stops a long label forcing a wrap. */}
      <div className="mt-3 flex items-stretch gap-1">
        {!approved && (
          <Button
            variant="success"
            size="xs"
            className="min-w-0 flex-1"
            disabled={working || !asset.preview_url}
            title={asset.preview_url ? "Approve this image" : "Nothing to approve until the image is extracted"}
            onClick={() => act("approve")}
          >
            Approve
          </Button>
        )}
        <label
          title="Upload your own image for this question"
          className="flex min-w-0 flex-1 cursor-pointer items-center justify-center rounded-md border border-white/15 bg-white/5 px-2 py-1 text-center text-[11px] font-medium text-white/80 transition-colors hover:bg-white/10"
        >
          Replace
          <input
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && replace(e.target.files[0])}
          />
        </label>
        <Button
          size="xs"
          className="min-w-0 flex-1"
          disabled={working}
          title="Show Claude the page and this crop, and crop again from corrected coordinates"
          onClick={() => act("reextract")}
        >
          Fix Crop
        </Button>
      </div>

      {/* On its own line: it opens a panel rather than changing the asset, so it is a
          different kind of action from the three above — and giving it the full width
          leaves room for the label to say what it actually does. */}
      {asset.page_url && (
        <Button
          variant="ghost"
          size="xs"
          className="mt-1.5 w-full"
          onClick={() => setShowSource((v) => !v)}
        >
          {showSource ? "Hide Source Page" : `View Source Page ${asset.source_page ?? ""}`.trim()}
        </Button>
      )}

      {showSource && asset.page_url && (
        <div className="mt-3 overflow-hidden rounded-lg border border-white/10 bg-black/30">
          <AuthImage
            src={`${API_BASE_URL}${asset.page_url}`}
            alt={`Source page ${asset.source_page}`}
            className="max-h-[520px] w-full object-contain"
            emptyLabel="Could not load the source page"
          />
        </div>
      )}
    </div>
  );
};

const AssetsPanel = ({ paper, onChanged, onExtractOnly, onRefineAll, onReload, refreshing, extracting, highlight, autoOpen }) => {
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("all");

  const assets = paper.assets || [];

  // Extracting everything belongs to the pipeline panel; this used to offer the same
  // action under a different name, so two buttons on one screen did the same work.
  // What is useful HERE is the scoped version: retry only what did not come out.
  const broken = assets.filter(
    (a) => a.extraction_status === "failed" || a.crop_verification === "failed" || !a.path
  );
  const extracted = assets.filter((a) => a.path).length;
  // Anything the verifier was not happy with, and that a human has not signed off.
  const needsFixing = assets.filter(
    (a) => a.human_review_status !== "approved" && a.crop_verification && a.crop_verification !== "passed"
  );
  const approved  = assets.filter((a) => a.human_review_status === "approved").length;
  const filters = [
    ["all", "All", assets.length],
    ["pending", "Needs Review", assets.filter((a) => a.human_review_status !== "approved").length],
    ["problem", "Problem", assets.filter((a) => a.extraction_status === "failed" || a.crop_verification === "failed").length],
    ["approved", "Approved", assets.filter((a) => a.human_review_status === "approved").length],
  ];

  const visible = assets.filter((a) => {
    if (filter === "pending")  return a.human_review_status !== "approved";
    if (filter === "approved") return a.human_review_status === "approved";
    if (filter === "problem")  return a.extraction_status === "failed" || a.crop_verification === "failed";
    return true;
  });

  return (
    <Panel
      highlight={highlight}
      autoOpen={autoOpen}
      collapsible
      // Open only while this section still needs someone. Every crop approved means
      // the work is done, and a finished section standing open on page load buries
      // the one that actually wants attention.
      defaultOpen={needsFixing.length > 0 || broken.length > 0 || approved < assets.length}
      title={`Visual Assets — ${assets.length}`}
      subtitle={
        paper.extraction_available === false ? paper.extraction_note
        : extracted === 0 ? `${assets.length} detected, none extracted yet`
        : `${extracted} of ${assets.length} extracted · ${approved} approved`
      }
      action={
        // Refresh sits here as well as in the page header: this panel is where the
        // long crop runs land, and it is a long scroll back up to the other one.
        <div className="flex items-center gap-1">
          {onReload && (
            <Button
              variant="ghost"
              size="sm"
              disabled={refreshing}
              onClick={onReload}
              title="Re-read this paper from the server"
            >
              {refreshing ? "Refreshing…" : "↻ Refresh"}
            </Button>
          )}
          {needsFixing.length > 0 ? (
            <Button
              variant="secondary"
              disabled={extracting}
              onClick={() => onRefineAll()}
              title="Re-derive every unapproved crop by looking at the page"
            >
              {extracting ? "Fixing…" : `Fix ${needsFixing.length} Crops`}
            </Button>
          ) : broken.length > 0 ? (
            <Button
              variant="secondary"
              disabled={extracting}
              onClick={() => onExtractOnly(broken.map((a) => a.asset_id))}
              title="Re-crop only the images that failed, leaving approved ones untouched"
            >
              {extracting ? "Extracting…" : `Retry ${broken.length} Failed`}
            </Button>
          ) : null}
        </div>
      }
    >
      {assets.length === 0 ? (
        <p className="text-sm text-white/50">No visual assets were detected in this paper.</p>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            {filters.map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                onClick={() => setFilter(key)}
                className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                  filter === key
                    ? "border-[#2e92b0] bg-[#2e92b0]/20 text-white"
                    : "border-white/15 bg-white/5 text-white/60 hover:bg-white/10"
                }`}
              >
                {label} ({count})
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {visible.map((asset) => (
              <AssetCard
                key={asset.asset_id}
                asset={asset}
                paperId={paper.id}
                onChanged={onChanged}
                busy={busy}
                setBusy={setBusy}
              />
            ))}
          </div>

          {visible.length === 0 && (
            <p className="text-sm text-white/50">Nothing in this filter.</p>
          )}
        </>
      )}
    </Panel>
  );
};

export default AssetsPanel;
